/**
 * Ask the Blocks AI Agent which parts of an uploaded .docx are fill-in fields.
 *
 * The agent's system prompt and output schema live server-side in the Blocks console — see
 * `docs` in the plan, and `VITE_DOCX_FIELD_SUGGESTIONS_AGENT_ID`. Nothing here trusts the
 * response: it is parsed leniently, coerced field by field, and anything unusable is dropped
 * rather than raised. Positional data is not even requested; `selection/ai-anchor.ts` derives
 * every span from the document itself.
 *
 * Normalization follows the house pattern established by `contract-analysis-ai.service.ts`:
 * small `coerce*` primitives, a per-entity `normalize*` that returns null for rows it cannot
 * use, and a top-level normalizer that filters. Partial output degrades; it never throws.
 */

import { queryBlocksAgent } from '@/lib/blocks-ai-agent';
import {
  COMPANY_SUB_KINDS,
  SUB_KINDS,
  TYPE_OPTIONS,
  type CompanySubKind,
  type ScalarType,
  type SubKind,
} from '@/modules/org/components/docx-builder/selection/types';
import type { DocumentParagraph } from '@/modules/org/components/docx-builder/selection/ai-anchor';
import { buildDocxFieldSuggestionsPrompt } from '@/modules/templates/lib/build-docx-field-suggestions-prompt';
import { parseJsonResponse } from './ai-utils';
import {
  EMPTY_SUGGESTIONS,
  type DocxFieldSuggestions,
  type SuggestedCompany,
  type SuggestedCompanySubField,
  type SuggestedField,
  type SuggestedPerson,
  type SuggestedPersonSubField,
} from './docx-field-suggestions.types';

const AGENT_ID = import.meta.env.VITE_DOCX_FIELD_SUGGESTIONS_AGENT_ID;

/** Ceiling on what one run may propose, so a runaway response cannot flood the panel. */
const MAX_FIELDS = 80;
const MAX_ENTITIES = 20;

const SCALAR_TYPES = new Set<string>(TYPE_OPTIONS.map((o) => o.value));
const PERSON_SLOTS = new Set<string>(SUB_KINDS);
const COMPANY_SLOTS = new Set<string>(COMPANY_SUB_KINDS);

// ---------------------------------------------------------------------------
// Coercion primitives
// ---------------------------------------------------------------------------

function coerceString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/** Text spans keep their internal whitespace — only the ends are trimmed. */
function coerceSpanText(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  return value.trim().length > 0 ? value : undefined;
}

function coerceIndex(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0) return value;
  if (typeof value === 'string') {
    const n = Number(value);
    if (Number.isInteger(n) && n >= 0) return n;
  }
  return undefined;
}

function coerceBoolean(value: unknown, fallback: boolean): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const lower = value.toLowerCase();
    if (lower === 'true' || lower === 'yes') return true;
    if (lower === 'false' || lower === 'no') return false;
  }
  return fallback;
}

function coerceType(value: unknown): ScalarType {
  const s = coerceString(value)?.toLowerCase();
  return s && SCALAR_TYPES.has(s) ? (s as ScalarType) : 'text';
}

function coerceOptions(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const out = value.map(coerceString).filter((v): v is string => !!v);
  return out.length > 0 ? out : undefined;
}

// ---------------------------------------------------------------------------
// Entity normalization
// ---------------------------------------------------------------------------

/** The span fields every suggestion shares, or null if it has no usable text/citation. */
function coerceSpan(obj: Record<string, unknown>, defaultMirror: boolean) {
  const text = coerceSpanText(obj.text);
  const paragraphIndex = coerceIndex(obj.paragraphIndex);
  if (!text || paragraphIndex === undefined) return null;
  return { text, paragraphIndex, mirrorRepeats: coerceBoolean(obj.mirrorRepeats, defaultMirror) };
}

function normalizeField(entry: unknown): SuggestedField | null {
  if (!entry || typeof entry !== 'object') return null;
  const obj = entry as Record<string, unknown>;

  const span = coerceSpan(obj, false);
  const label = coerceString(obj.label);
  if (!span || !label) return null;

  const type = coerceType(obj.type);
  const options = type === 'select' ? coerceOptions(obj.options) : undefined;
  // A dropdown with no choices cannot be filled in; demote it to plain text rather than drop it.
  const resolvedType: ScalarType = type === 'select' && !options ? 'text' : type;

  return {
    ...span,
    label,
    type: resolvedType,
    required: coerceBoolean(obj.required, true),
    placeholder: coerceString(obj.placeholder),
    helpText: coerceString(obj.helpText),
    currencyCode: resolvedType === 'currency' ? coerceString(obj.currencyCode) : undefined,
    options,
  };
}

/**
 * Sub-fields for one entity: unknown slots are dropped, and a repeated slot keeps only its
 * first occurrence — the marking model allows exactly one field per slot per entity.
 */
function normalizeSubFields<S extends string>(
  raw: unknown,
  allowed: Set<string>
): Array<{ slot: S; text: string; paragraphIndex: number; mirrorRepeats: boolean }> {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: Array<{ slot: S; text: string; paragraphIndex: number; mirrorRepeats: boolean }> = [];

  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const obj = entry as Record<string, unknown>;
    const slot = coerceString(obj.slot)?.toLowerCase();
    if (!slot || !allowed.has(slot) || seen.has(slot)) continue;
    // An entity's name is its identity and repeats throughout the document, so mirror by default.
    const span = coerceSpan(obj, slot === 'name');
    if (!span) continue;
    seen.add(slot);
    out.push({ slot: slot as S, ...span });
  }
  return out;
}

/** A person is unusable without a name slot — `save()` refuses one, so drop it here. */
function normalizePerson(entry: unknown): SuggestedPerson | null {
  if (!entry || typeof entry !== 'object') return null;
  const obj = entry as Record<string, unknown>;

  const label = coerceString(obj.label);
  const subFields = normalizeSubFields<SubKind>(obj.subFields, PERSON_SLOTS);
  if (!label || !subFields.some((s) => s.slot === 'name')) return null;

  const isSignatory = coerceBoolean(obj.isSignatory, false);
  const order = coerceIndex(obj.signingOrder);

  return {
    label,
    isSignatory,
    // signingOrder is renumbered to a contiguous 1..N at save time, so a rough value is fine.
    signingOrder: isSignatory ? (order && order > 0 ? order : 1) : null,
    subFields: subFields as SuggestedPersonSubField[],
  };
}

function normalizeCompany(entry: unknown): SuggestedCompany | null {
  if (!entry || typeof entry !== 'object') return null;
  const obj = entry as Record<string, unknown>;

  const label = coerceString(obj.label);
  const subFields = normalizeSubFields<CompanySubKind>(obj.subFields, COMPANY_SLOTS);
  if (!label || !subFields.some((s) => s.slot === 'name')) return null;

  return { label, subFields: subFields as SuggestedCompanySubField[] };
}

export function normalizeSuggestions(parsed: Record<string, unknown>): DocxFieldSuggestions {
  return {
    fields: (Array.isArray(parsed.fields) ? parsed.fields : [])
      .map(normalizeField)
      .filter((f): f is SuggestedField => f !== null)
      .slice(0, MAX_FIELDS),
    people: (Array.isArray(parsed.people) ? parsed.people : [])
      .map(normalizePerson)
      .filter((p): p is SuggestedPerson => p !== null)
      .slice(0, MAX_ENTITIES),
    companies: (Array.isArray(parsed.companies) ? parsed.companies : [])
      .map(normalizeCompany)
      .filter((c): c is SuggestedCompany => c !== null)
      .slice(0, MAX_ENTITIES),
  };
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

/**
 * Cheap check for a reply cut off at the agent's max_tokens. Mirrors
 * `generate-document-via-agent.ts`: the JSON repair path can spend seconds balancing a large
 * truncated blob on the main thread, so it is better to fail fast with actionable advice.
 */
function isLikelyTruncated(message: string): boolean {
  const trimmed = message.trim();
  if (trimmed.length === 0) return true;
  const body = trimmed.endsWith('```') ? trimmed.slice(0, -3).trimEnd() : trimmed;
  if (!body.endsWith('}')) return true;
  let open = 0;
  let close = 0;
  for (let i = 0; i < body.length; i++) {
    const c = body.charCodeAt(i);
    if (c === 123) open++;
    else if (c === 125) close++;
  }
  return open - close > 5;
}

export interface SuggestDocxFieldsInput {
  paragraphs: DocumentParagraph[];
  documentName?: string;
  signal?: AbortSignal;
}

export interface SuggestDocxFieldsResult {
  suggestions: DocxFieldSuggestions;
  /** Paragraphs left out of the prompt to stay inside the size budget. */
  omittedParagraphs: number;
}

/**
 * Run one suggestion pass. Retries and auth live in `queryBlocksAgent`; the caller owns the
 * timeout, because the transport deliberately has no wall-clock limit of its own.
 */
export async function suggestDocxFields(
  input: SuggestDocxFieldsInput
): Promise<SuggestDocxFieldsResult> {
  if (!AGENT_ID) {
    throw new Error(
      'Field suggestions are not configured — VITE_DOCX_FIELD_SUGGESTIONS_AGENT_ID is missing.'
    );
  }
  if (input.paragraphs.length === 0) {
    return { suggestions: EMPTY_SUGGESTIONS, omittedParagraphs: 0 };
  }

  const { message, omittedParagraphs } = buildDocxFieldSuggestionsPrompt(
    input.paragraphs,
    input.documentName
  );

  const response = await queryBlocksAgent(AGENT_ID, message, input.signal);
  if (!response?.message) {
    throw new Error('The AI returned an empty response. Please try again.');
  }
  if (isLikelyTruncated(response.message)) {
    throw new Error(
      "The AI response was cut off before it finished. Increase the agent's max_tokens (or try a shorter document)."
    );
  }

  let parsed: Record<string, unknown>;
  try {
    parsed = parseJsonResponse(response.message);
  } catch {
    throw new Error('The AI response could not be read. Please try again.');
  }

  return { suggestions: normalizeSuggestions(parsed), omittedParagraphs };
}
